import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Section,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const GlobalOverview = () => (
  <Page
    title="Global Overview"
    intro="The Global Overview is a management dashboard summarising the whole
    organisation on one screen — workforce, shifts, incidents, fleet, KPIs,
    and anything needing attention. Open it from the sidebar entry just below
    Home, or go to /dashboard directly."
  >
    <Section title="Who can do this?">
      <p>
        The Global Overview is available to members of the{' '}
        <strong>Manager</strong> or <strong>Supervisor</strong> teams, and to
        Site Administrators. Everyone else gets a polite "not available"
        message, and the sidebar entry is hidden.
      </p>
    </Section>

    <Section title="Today at a glance">
      <p>
        The header greets you with today's headline numbers — shifts today,
        shifts in the next 7 days, open incidents, and the percentage of the
        fleet that's operational.
      </p>
      <p>
        Below it, six stat tiles each click through to the page where you can
        act:
      </p>
      <Bullets>
        <Bullet>
          <strong>Live staff</strong> (with new starters) — opens the workforce
          dashboard
        </Bullet>
        <Bullet>
          <strong>Shifts next 7 days</strong> (with today's count) — opens the
          Global Roster
        </Bullet>
        <Bullet>
          <strong>Uncovered shifts</strong> in the next 7 days — opens the
          Global Roster
        </Bullet>
        <Bullet>
          <strong>Open incidents</strong> (with a 30-day count) — opens
          Incidents
        </Bullet>
        <Bullet>
          <strong>Patient records</strong> submitted in the last 30 days —
          opens patient report forms
        </Bullet>
        <Bullet>
          <strong>Shift applications</strong> awaiting review — opens Shift
          Applications
        </Bullet>
      </Bullets>
    </Section>

    <Section title="KPI health">
      <p>
        The KPI health strip shows an overall score for each KPI area over the
        last 30 days, using the same data as the full KPI dashboard. Click an
        area to open its detailed KPI page.
      </p>
      <Info>
        KPI health draws on the KPI dashboard, which is available to the
        Governance, Manager, and Supervisor teams (and Site Administrators).
      </Info>
    </Section>

    <Section title="Charts and fleet readiness">
      <Bullets>
        <Bullet>
          <strong>Shift tempo</strong> — shifts per week, so you can see
          workload building or easing
        </Bullet>
        <Bullet>
          <strong>Incidents trend</strong> — incidents reported per month
        </Bullet>
        <Bullet>
          <strong>Fleet readiness</strong> — operational vehicles against
          those off the road, with open defect counts and a click-through to
          Defects
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Needs your attention">
      <p>
        The attention rail collects the things most worth acting on now, each
        flagged as <strong>Critical</strong> or <strong>Attention</strong> and
        linked straight to the right page:
      </p>
      <Bullets>
        <Bullet>Expired medicines still in circulation</Bullet>
        <Bullet>Vehicle services overdue</Bullet>
        <Bullet>Uncovered shifts in the next 7 days</Bullet>
        <Bullet>Safety alerts awaiting acceptance</Bullet>
        <Bullet>Incidents open for investigation</Bullet>
        <Bullet>Shift applications to review</Bullet>
        <Bullet>New patient feedback</Bullet>
        <Bullet>Medicines expiring within 30 days</Bullet>
        <Bullet>New defects reported</Bullet>
        <Bullet>Employee applications submitted</Bullet>
      </Bullets>
      <Info title="How fresh is it?">
        The overview is a snapshot generated when the page loads — the
        generation time is shown at the bottom right. Reload the page for
        up-to-the-minute numbers.
      </Info>
    </Section>

    <Section title="Related dashboards">
      <p>
        Personnel team members also have a dedicated workforce dashboard
        (sidebar: <strong>People &gt; Dashboard</strong>, the People Dashboard)
        covering staffing,
        compliance, and outstanding HR work. For your own day-to-day view, see{' '}
        <DocLink
          docsPage
          url="/getting-started/home-screen"
          text="Home Screen"
        />
        .
      </p>
    </Section>
  </Page>
)

export default GlobalOverview
